import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { getImportedArticleCountForWorkspace } from "./freeTierTrackerslice"
import {openPrimaryWorkspace, resetWorkspace,} from "./workspaceSlice"
import {setUserAsync} from "./userSlice"

export const VIEW = 0

export const MANAGE_WORKSPACE_VIEW = {
	TRANSFER_OWNERSHIP: 0,
	MANAGE_MEMBERS: 1,
	ADD_NEW_MEMBER: 2,
	INVITE_AUTHOR: 3,
	WORKSPACE_PERMISSIONS: 4,
	NOTE_PERMISSIONS: 5,
	MANAGE_INVITES: 6,
	MANAGE_MEMBER: 7,
}

/*
 *
 *		General User Management Thunks
 *
 */
export const fetchWorkspaceMembers = createAsyncThunk(
	"manageWorkspaceSlice/fetchWorkspaceMembers",
	async (_, {getState, rejectWithValue}) => {
		const state = await getState()
		const workspace = state.workspace.byWorkspace[state.workspace.primaryId]
		try {
			const {data: workspaceMembers} = await axios.get(`/api/workspace/${workspace.id}/user`)
			return workspaceMembers
		} catch (e) {
			console.error("RETRIEVING WORKSPACE MEMBERS & PERMISSIONS: \n", e)
			return rejectWithValue(e)
		}
	}
)

export const confirmWorkspaceTransfer = createAsyncThunk(
	"manageWorkspaceSlice/confirmWorkspaceTransfer",
	async ({userId, workspaceId}, {dispatch, rejectWithValue}) => {
		try {
			const url = `/api/workspace/${workspaceId}/transferOwnership/user/${userId}`
			const res = await axios.put(url)
			const {data: updatedWorkspace} = res
			dispatch(getImportedArticleCountForWorkspace({updatedWorkspace}))
			return updatedWorkspace
		} catch (error) {
			if (error.response) {
				return rejectWithValue(error.response.statusText)
			}
			return rejectWithValue(error.message)
		}
	}
)

export const leaveWorkspace = createAsyncThunk(
	"manageWorkspaceSlice/leaveWorkspace",
	async (_, {dispatch, getState, rejectWithValue}) => {
		const state = await getState()
		const workspace = state.workspace.byWorkspace[state.workspace.primaryId]
		try {
			await axios.delete(`/api/workspace/${workspace.id}/self`)
			await dispatch(setUserAsync())
			await dispatch(resetWorkspace(workspace.id))
		}
		catch(e) {
			if(e.response.status === 515){
				return rejectWithValue(e.response.statusText)
			} else {
				console.error("MODIFYING WORKSPACE MEMBER PERMISSIONS: \n", e)
				return rejectWithValue(`Failed to leave workspace. Reason: ${e}`)
			}
		}
	}
)
/*
 *
 *		Modify User Members Thunks
 *
 */
export const fetchMemberUserToModify = createAsyncThunk(
	"manageWorkspaceSlice/fetchMemberUserToModify",
	async (member, {getState, rejectWithValue}) => {
		const {user} = member
		const currentState = getState()
		const workspaceId = currentState.workspace.primaryId
		try {
			const {data: selectedNoteIds} = await axios.get(`/api/workspace/${workspaceId}/user/${user.id}/restrictions`)
			const {data: isFreeTier} = await axios.get(`/api/workspace/${workspaceId}/member/${user.id}/is-free`)

			return { ...member, selectedNoteIds, isFreeTier }
		} catch (e) {
			console.error("FAILED TO FETCH MEMBER TO MODIFY: ", e)
			return rejectWithValue(e.message)
		}
	}
)

export const removeUserFromWorkspace = createAsyncThunk(
	"manageWorkspaceSlice/removeUserFromWorkspace",
	async (_, {rejectWithValue, dispatch, getState}) => {
		const state = await getState()
		const workspace = state.workspace.byWorkspace[state.workspace.primaryId]
		const targetUser = state.manageWorkspaceSlice?.targetUser
		const {id: userId} = targetUser.user
		try {
			await axios.delete(`/api/workspace/${workspace.id}/user/${userId}`)
			await dispatch(fetchWorkspaceMembers())
		} catch (e) {
			if(e.response.status === 514){
				return rejectWithValue(e.response.statusText)
			} else {
				console.error("MODIFYING WORKSPACE MEMBER PERMISSIONS: \n", e)
				return rejectWithValue(`Failed to modify workspace member's permissions. Reason: ${e}`)
			}
		}
	}
)

export const revokeAuthorInvite = createAsyncThunk(
	"manageWorkspaceSlice/revokeAuthorInvite",
	async (_, {rejectWithValue, dispatch, getState}) => {
		const state = await getState()
		const targetAuthor = state.manageWorkspaceSlice?.targetUser
		try {
			await axios.delete(`/api/license/author-invite/${targetAuthor.user.invite.id}}`)
			dispatch(setManageWorkspaceMessage("Successfully revoked author invite"))
			dispatch(fetchAuthorInviteDTOsForWorkspace())
		} catch (e) {
			console.error("REVOKING AUTHOR INVITE: \n", e)
			if(e.response.status === 518){
				return rejectWithValue(e.response.statusText)
			} else {
				return rejectWithValue(`Failed to revoke author invite. Reason: ${e}`)
			}
		}
	}
)

export const modifyExistingWorkspaceMember = createAsyncThunk(
	"manageWorkspaceSlice/modifyExistingWorkspaceMember",
	async (targetMember, {rejectWithValue, getState, dispatch}) => {
		const state = await getState()
		const workspace = state.workspace.byWorkspace[state.workspace.primaryId]
		try {
			await axios.put(`/api/workspace/${workspace.id}/user/${targetMember.user.id}`, targetMember)
			dispatch(clearManageWorkspaceView())
		} catch (e) {
			if(e.response.status === 514){
				return rejectWithValue(e.response.statusText)
			} else if (e.response.status === 401 && e.response.data){
				return rejectWithValue(e.response.data)
			} else {
				console.error("ERROR MODIFYING WORKSPACE MEMBER PERMISSIONS: \n", e)
				return rejectWithValue(`Failed to modify workspace member's permissions. Reason: ${e}`, {variant: "error"})
			}
		}
	}
)

export const addNewWorkspaceMember = createAsyncThunk(
	"manageWorkspaceSlice/addNewWorkspaceMember",
	async(targetMember, {rejectWithValue, getState, dispatch}) => {
		const state = await getState()
		const workspace = state.workspace.byWorkspace[state.workspace.primaryId]
		try {
			//await axios.post(`/api/workspace/${workspace.id}/user/${targetMember.user.id}`, targetMember)
			await axios.post(`/api/notifications/send-invite/${targetMember.user.id}/${workspace.id}/`, targetMember)
			dispatch(clearManageWorkspaceView())
		} catch (e) {
			if(e.response.status === 514){
				return rejectWithValue(e.response.statusText)
			} else if (e.response.status === 401 && e.response.data){
				return rejectWithValue(e.response.data)
			} else {
				console.error("ERROR ADDING NEW WORKSPACE MEMBER PERMISSIONS: \n", e)
				return rejectWithValue(`Failed to add new workspace member. Reason: ${e}`, {variant: "error"})
			}
		}
	}
)

export const inviteNewWorkspaceMember = createAsyncThunk(
	"manageWorkspaceSlice/inviteNewWorkspaceMember",
	async (userToInvite, {rejectWithValue, getState, }) => {
		try {
			const state = await getState()
			const {id: workspaceId} = state.workspace.primaryId
			await axios.post(`/api/notifications/send-invite/${userToInvite.id}/${workspaceId}/`)
		} catch (e) {
			console.error("ERROR INVITING NEW WORKSPACE MEMBER: ", e)
			return rejectWithValue(e.response.statusText)
		}
	}
)

/*
 *
 *		Author Invite Management Thunks
 *
 */
export const fetchAuthorInviteDTOsForWorkspace = createAsyncThunk(
	"manageWorkspaceSlice/fetchAuthorInviteDTOsForWorkspace",
	async (_, {getState, rejectWithValue}) => {
		try {
			const currentState = getState()
			const workspaceId = currentState.workspace.primaryId
			const {data: authorInviteDTOs} = await axios.get(`/api/workspace/${workspaceId}/author-invites`)
			return authorInviteDTOs
		} catch (error) {
			if(error.response) {
				return rejectWithValue(error.response.statusText)
			} else {
				return rejectWithValue(error.message)
			}
		}
	}
)

export const generateAuthorInvite = createAsyncThunk(
	"manageWorkspaceSlice/generateAuthorInvite",
	async (targetUser, {getState, rejectWithValue}) => {
		const currentState = getState()
		const workspaceId = currentState.workspace.primaryId
		// needs to be the shape of AuthorInviteConstructor
		const authorInviteConstructor = {
			workspaceId,
			authorEmail: targetUser.user.email,
			noteRestrictions: targetUser.selectedNoteIds,
			workspacePermissions: targetUser.workspacePermissions
		}
		try {
			const {data: inviteObject} = await axios.post("/api/license/invite-author", authorInviteConstructor)
			return inviteObject
		} catch (error) {
			console.error("FAILED TO INVITE AUTHOR: ", error)
			if(error.response) {
				return rejectWithValue(error.response.statusText)
			} else {
				return rejectWithValue(error.message)
			}
		}
	}
)

/**
 * Modifies an existing author invite with a new inviteEmail or note-permissions.
 * PATCH payload must take the form of a ModifyAuthorInviteDTO
 *
 * @param targetAuthorUser - ModifyAuthorInviteDTO
 */
export const modifyAuthorInvite = createAsyncThunk(
	"manageWorkspaceSlice/modifyAuthorInvite",
	async (_, { rejectWithValue, getState}) => {
		const currentState = getState()
		const targetAuthorInviteDTO = currentState.manageWorkspaceSlice.targetUser
		try {
			const {data: inviteObject} = await axios.patch("/api/license/author-invite", targetAuthorInviteDTO)
			return inviteObject
		} catch (error) {
			console.error("FAILED TO INVITE AUTHOR: ", error)
			if(error.response) {
				return rejectWithValue(error.response.statusText)
			} else {
				return rejectWithValue(error.message)
			}
		}
	}
)

/*
 *
 *		Other Thunks required for WS management
 *
 */
export const fetchTopLevelNotesForManagement = createAsyncThunk(
	"manageWorkspaceSlice/fetchTopLevelNotesForManagement",
	async (_, {getState, rejectWithValue}) => {
		const currentState = getState()
		const workspaceId = currentState.workspace.primaryId
		try {
			const {data: topLevelNotes} = await axios.get(`/api/workspace/${workspaceId}/note/0/children`)
			return { topLevelNotes }
		} catch (e) {
			console.error("ERROR FETCHING TOP LEVEL NOTES FOR MODIFYING NOTE PERMISSIONS: ", e)
			if(e.response) {
				return rejectWithValue(e.response.statusText)
			} else {
				return rejectWithValue(e.message)
			}
		}
	}
)
// Initial state
const initialState = {
	view: MANAGE_WORKSPACE_VIEW.MANAGE_MEMBERS,
	targetUser: null,
	message: null,
	error: null,
	loading: false,
	authorInviteDTOs: [],
	workspaceMembers: [],
	showConfirmLeave: false,
	topLevelNotes: [],
	isAddingNewMember: false,
	isInvitingAuthor: false,
	isLeavingWorkspace: false,
	isModifyingAuthor: false,
}
// Create slice
const manageWorkspaceSlice = createSlice({
	name: "manageWorkspaceSlice",
	initialState,
	reducers: {
		setManageWorkspaceView: (state, action) => {
			state.view = action.payload
		},
		setManageWorkspaceError: (state, action) => {
			state.error = action.payload
		},
		clearError: (state) => {
			state.error = null
		},
		setManageWorkspaceMessage: (state, action) => {
			state.message = action.payload
		},
		clearMessage: (state) => {
			state.message = null
		},
		setTargetUser: (state, action) => {
			state.targetUser = action.payload
		},
		clearTargetUser: (state) => {
			state.targetUser = null
		},
		setWorkspaceMembers: (state, action) => {
			state.workspaceMembers = action.payload
		},
		clearManageWorkspaceView: (state) => {
			state.view = MANAGE_WORKSPACE_VIEW.MANAGE_MEMBERS
			state.targetUser = null
			state.workspaceMembers = []
			state.authorInviteDTOs = []
			state.loading = false
			state.isAddingNewMember = false
			state.isInvitingAuthor = false
			state.isLeavingWorkspace = false
			state.isModifyingAuthor = false
		},
		clearAuthorInviteDTOs: (state) => {
			state.authorInviteDTOs = []
		},
		toggleConfirmLeave: (state) => {
			state.showConfirmLeave = !state.showConfirmLeave
		},
		setTopLevelNotes: (state, action) => {
			const {topLevelNotes} = action.payload
			state.topLevelNotes = topLevelNotes
		},
		resetTopLevelNotePermissions: (state) => {
			state.targetUser.selectedNoteIds = {}
		},
		setTopLevelNotePermissionsAllRead: (state) => {
			const fullReadNotesMap = state.topLevelNotes.reduce((acc, note) => {
				acc[note.id] = "read"
				return acc
			}, {})
			state.targetUser.selectedNoteIds = fullReadNotesMap
		},
		setTopLevelNotePermissionsAllEdit: (state) => {
			const fullReadNotesMap = state.topLevelNotes.reduce((acc, note) => {
				acc[note.id] = "edit"
				return acc
			}, {})
			state.targetUser.selectedNoteIds = fullReadNotesMap
		},
		setTopLevelNotePermission: (state, action) => {
			// action.payload shape should be {noteId: number, permissionType: "read" || "edit"
			const {noteId, newPermissionType} = action.payload
			const topLevelNotePermissionsClone = state.targetUser.selectedNoteIds
			topLevelNotePermissionsClone[noteId] = newPermissionType
			state.targetUser.selectedNoteIds = topLevelNotePermissionsClone
		},
		removeTopLevelNotePermission: (state, action) => {
			const {noteId} = action.payload
			const topLevelNotePermissionsClone = state.targetUser.selectedNoteIds
			delete topLevelNotePermissionsClone[noteId]

			state.targetUser.selectedNoteIds = topLevelNotePermissionsClone
		},
		setIsAddingNewMember: (state, action) => {
			state.isAddingNewMember = action.payload
		},
		setIsInvitingAuthor: (state, action) => {
			state.isInvitingAuthor = action.payload
		},
		setIsLeavingWorkspace: (state, action) => {
			state.isLeavingWorkspace = action.payload
		},
		setIsModifyingAuthor: (state, action) => {
			state.isModifyingAuthor = action.payload
		},
		setUpdatedInviteEmailValue: (state, action) => {
			if(state.targetUser.user.invite) {
				state.targetUser.updatedEmail = action.payload
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				confirmWorkspaceTransfer.fulfilled,
				(state) => {
					state.message = "Ownership transfer was successful"
					state.view = MANAGE_WORKSPACE_VIEW.MANAGE_MEMBERS
					state.targetUser = null
					state.workspaceMembers = []
					state.authorInviteDTOs = []
				}
			)
			.addCase(
				confirmWorkspaceTransfer.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload || "Ownership transfer failed"
				}
			)
			.addCase(
				fetchAuthorInviteDTOsForWorkspace.fulfilled,
				(state, action) => {
					state.authorInviteDTOs = action.payload
				}
			)
			.addCase(
				fetchAuthorInviteDTOsForWorkspace.rejected,
				(state, action) => {
					state.error = action.payload
				}
			)
			.addCase( // If user creates or moves a new workspace - clear authorInviteDTOs
				openPrimaryWorkspace.fulfilled,
				manageWorkspaceSlice.caseReducers.clearManageWorkspaceView
			)
			.addCase(
				fetchWorkspaceMembers.fulfilled,
				manageWorkspaceSlice.caseReducers.setWorkspaceMembers
			)
			.addCase(
				fetchWorkspaceMembers.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload
				}
			)
			.addCase(
				removeUserFromWorkspace.fulfilled,
				(state) => {
					if(state.loading) {
						state.loading = false
					}
				}
			)
			.addCase(
				removeUserFromWorkspace.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload
				}
			)
			.addCase(
				leaveWorkspace.fulfilled,
				manageWorkspaceSlice.caseReducers.clearManageWorkspaceView
			)
			.addCase(
				leaveWorkspace.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload
				}
			)
			.addCase(
				fetchMemberUserToModify.fulfilled,
				manageWorkspaceSlice.caseReducers.setTargetUser
			)
			.addCase(
				fetchMemberUserToModify.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload || "Failed to retrieve member"
				}
			)
			.addCase(
				fetchTopLevelNotesForManagement.fulfilled,
				manageWorkspaceSlice.caseReducers.setTopLevelNotes
			)
			.addCase(
				fetchTopLevelNotesForManagement.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload || "Failed to retrieve top-level notes for workspace"
				}
			)
			.addCase(
				addNewWorkspaceMember.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload
				}
			)
			.addCase(
				revokeAuthorInvite.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload || "Failed to revoke Author Invite"
				}
			)
			.addCase(
				inviteNewWorkspaceMember.rejected,
				(state, action) => {
					state.loading = false
					state.error = action.payload || "Failed to invite New User To Workspace"
				}
			)
	}
})

// Export actions generated from createSlice
export const {
	setManageWorkspaceView,
	clearManageWorkspaceView,
	setTargetUser,
	clearMessage,
	clearTargetUser,
	resetTopLevelNotePermissions,
	setTopLevelNotePermissionsAllRead,
	setTopLevelNotePermissionsAllEdit,
	setTopLevelNotePermission,
	removeTopLevelNotePermission,
	setIsAddingNewMember,
	setIsInvitingAuthor,
	setIsLeavingWorkspace,
	setIsModifyingAuthor,
	setUpdatedInviteEmailValue,
	setManageWorkspaceError,
	setManageWorkspaceMessage,
	clearError
} = manageWorkspaceSlice.actions

export const selectManageWorkspaceView = (state) => state.manageWorkspaceSlice.view
export const selectTargetUser = (state) => state.manageWorkspaceSlice.targetUser
export const selectAuthorInviteDTOs = (state) => state.manageWorkspaceSlice.authorInviteDTOs
export const selectWorkspaceMembers = (state) => state.manageWorkspaceSlice.workspaceMembers

export const selectMessage= (state) => state.manageWorkspaceSlice.message
export const selectError= (state) => state.manageWorkspaceSlice.error
export const selectManageWorkspaceLoading= (state) => state.manageWorkspaceSlice.loading
export const selectTopLevelNotes = (state) => state.manageWorkspaceSlice.topLevelNotes
export const selectUserNoteRestrictions = (state) => state.manageWorkspaceSlice.targetUser.selectedNoteIds
export const selectIsAddingNewMember = (state) => state.manageWorkspaceSlice.isAddingNewMember
export const selectIsInvitingAuthor = (state) => state.manageWorkspaceSlice.isInvitingAuthor
export const selectIsLeavingWorkspace = (state) => state.manageWorkspaceSlice.isLeavingWorkspace
export const selectIsModifyingAuthor = (state) => state.manageWorkspaceSlice.isModifyingAuthor

// Export the reducer
export default manageWorkspaceSlice.reducer
